import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../../../components/ui/button";
import { Card, CardContent } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { AdminPageHeader, StateBlock, StatusBadge } from "../components/AdminUi";
import { BRANDS, socialApi } from "./socialMarketingApi";

const EMPTY_INTEGRATIONS = { affidya: [], casadiriposoperte: [], rco: [] };

function cleanIntegrations(integrations) {
  const next = {};
  Object.entries(BRANDS).forEach(([brand]) => {
    next[brand] = (integrations[brand] || [])
      .map((item) => ({
        id: String(item.id || "").trim(),
        provider: String(item.provider || "").trim(),
        label: String(item.label || "").trim(),
      }))
      .filter((item) => item.id);
  });
  return next;
}

export default function SocialMarketingPostizModule() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [baseUrl, setBaseUrl] = useState("https://api.postiz.com/public/v1");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [integrations, setIntegrations] = useState(EMPTY_INTEGRATIONS);
  const [apiKey, setApiKey] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [meta, setMeta] = useState(null);
  const [remote, setRemote] = useState([]);
  const [freshToken, setFreshToken] = useState("");
  const [busy, setBusy] = useState("");

  const apply = (data) => {
    setBaseUrl(data.base_url || "https://api.postiz.com/public/v1");
    setWebhookUrl(data.webhook_url || "");
    setIntegrations({ ...EMPTY_INTEGRATIONS, ...(data.integrations || {}) });
    setMeta(data);
  };

  const load = useCallback(async () => {
    setError("");
    const data = await socialApi("/postiz");
    apply(data);
    return data;
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    socialApi("/postiz")
      .then((data) => { if (active) apply(data); })
      .catch((err) => { if (active) setError(err.message || "Impossibile caricare Postiz"); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const run = async (key, action) => {
    setBusy(key);
    try {
      await action();
    } catch (err) {
      toast.error(err.message || "Operazione non riuscita");
    } finally {
      setBusy("");
    }
  };

  const persist = async () => {
    const payload = {
      base_url: baseUrl,
      webhook_url: webhookUrl,
      integrations: cleanIntegrations(integrations),
    };
    if (apiKey.trim()) payload.api_key = apiKey.trim();
    if (webhookSecret.trim()) payload.webhook_secret = webhookSecret.trim();
    const data = await socialApi("/postiz", { method: "PUT", body: JSON.stringify(payload) });
    apply(data);
    setApiKey("");
    setWebhookSecret("");
    return data;
  };

  const save = () => run("save", async () => {
    await persist();
    toast.success("Configurazione Postiz salvata");
  });

  const test = () => run("test", async () => {
    const dirty = apiKey.trim()
      || webhookSecret.trim()
      || webhookUrl !== (meta?.webhook_url || "")
      || baseUrl !== (meta?.base_url || "");
    if (dirty) await persist();
    const data = await socialApi("/postiz/test", { method: "POST" });
    setRemote(data.integrations || []);
    toast.success(data.integrations?.length ? `Trovati ${data.integrations.length} canali` : "Connessione riuscita, nessun canale collegato");
  });

  const regenerate = () => run("token", async () => {
    if (!window.confirm("Il token condiviso precedente smette subito di funzionare. I bot che lo usano già vanno aggiornati. I token di un solo marchio restano validi. Generare un token nuovo per tutti i marchi?")) return;
    const data = await socialApi("/bot-token", { method: "POST" });
    setFreshToken(data.token);
    apply(data);
    toast.success("Token condiviso generato. Resta visibile anche in Procedura bot.");
  });

  const updateRow = (brand, index, patch) => {
    setIntegrations((current) => ({
      ...current,
      [brand]: (current[brand] || []).map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)),
    }));
  };

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Postiz"
        description="Collegamento usato dai bot Grok per pubblicare i post approvati. Affidya conserva la chiave e avvisa il bot; l'invio al social lo fa il bot tramite Postiz."
      />
      {loading ? <StateBlock state="loading" title="Caricamento della configurazione…" /> : null}
      {error ? <StateBlock state="error" title="Impossibile caricare Postiz" description={error} onRetry={() => load().then(() => setError("")).catch((err) => setError(err.message))} /> : null}

      {!loading && !error ? (
        <>
          <Card>
            <CardContent className="space-y-4 p-6">
              <div className="flex flex-wrap gap-2">
                <StatusBadge tone={meta?.api_key_set ? "success" : "warning"}>{meta?.api_key_set ? "Chiave API presente" : "Chiave API assente"}</StatusBadge>
                <StatusBadge tone={meta?.bot_token_set ? "success" : "warning"}>{meta?.bot_token_set ? "Token bot presente" : "Token bot assente"}</StatusBadge>
                <StatusBadge tone={meta?.webhook_url ? "info" : "neutral"}>{meta?.webhook_url ? "Avviso al bot attivo" : "Il bot controlla la coda"}</StatusBadge>
              </div>
              <div className="space-y-1">
                <Label htmlFor="postiz-base">URL API Postiz</Label>
                <Input id="postiz-base" value={baseUrl} onChange={(event) => setBaseUrl(event.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="postiz-key">Chiave API</Label>
                <Input id="postiz-key" type="password" autoComplete="off" value={apiKey} onChange={(event) => setApiKey(event.target.value)} placeholder={meta?.api_key_set ? `Salvata (${meta.api_key_preview}). Scrivi solo per sostituirla.` : "Incolla la chiave di Postiz"} />
                <p className="text-xs text-admin-muted">Su Postiz la chiave si crea nelle impostazioni API. Va nell'header Authorization, senza la parola Bearer.</p>
              </div>
              <div className="space-y-1">
                <Label htmlFor="postiz-hook">URL di avviso al bot</Label>
                <Input id="postiz-hook" value={webhookUrl} onChange={(event) => setWebhookUrl(event.target.value)} placeholder="https://… (facoltativo)" />
                <p className="text-xs text-admin-muted">Se è vuoto, il bot scopre i post approvati controllando la coda. Se è compilato, Affidya invia un avviso a ogni approvazione, modifica di un post già approvato, rifiuto o revoca.</p>
              </div>
              <div className="space-y-1">
                <Label htmlFor="postiz-secret">Segreto della firma</Label>
                <Input id="postiz-secret" type="password" autoComplete="off" value={webhookSecret} onChange={(event) => setWebhookSecret(event.target.value)} placeholder={meta?.webhook_secret_set ? `Salvato (${meta.webhook_secret_preview}). Scrivi solo per sostituirlo.` : "Facoltativo"} />
                <p className="text-xs text-admin-muted">Con il segreto, l'avviso porta l'header X-Social-Marketing-Signature: sha256=… calcolato sul corpo grezzo.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="button" onClick={save} disabled={busy === "save" || busy === "test"}>Salva</Button>
                <Button type="button" variant="outline" onClick={test} disabled={busy === "test" || busy === "save"}>Verifica connessione</Button>
                {meta?.api_key_set ? (
                  <Button type="button" variant="outline" onClick={() => run("clear-key", async () => {
                    const data = await socialApi("/postiz", { method: "PUT", body: JSON.stringify({ clear_api_key: true }) });
                    apply(data);
                    setApiKey("");
                    toast.success("Chiave API rimossa");
                  })}>Rimuovi chiave</Button>
                ) : null}
              </div>
              {remote.length ? (
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-admin-text">Canali trovati su Postiz</h3>
                  {remote.map((item) => (
                    <div key={item.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-admin-border p-3">
                      <div className="text-sm">
                        <div className="font-medium text-admin-text">{item.label || item.id}</div>
                        <div className="text-admin-muted">{item.provider || "piattaforma non indicata"} · {item.id}</div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {Object.values(BRANDS).map((itemBrand) => (
                          <Button key={itemBrand.id} type="button" size="sm" variant="outline" onClick={() => {
                            setIntegrations((current) => {
                              const rows = current[itemBrand.id] || [];
                              if (rows.some((row) => row.id === item.id)) return current;
                              return { ...current, [itemBrand.id]: [...rows, { id: item.id, provider: item.provider || "", label: item.label || "" }] };
                            });
                            toast.success(`Canale aggiunto a ${itemBrand.label}. Salva per confermare.`);
                          }}>
                            {itemBrand.label}
                          </Button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-5 p-6">
              <div>
                <h3 className="font-medium text-admin-text">Canali per marchio</h3>
                <p className="mt-1 text-sm text-admin-muted">
                  L'identificativo è l'id integrazione di Postiz. La piattaforma è il valore <span className="font-mono">__type</span> richiesto da Postiz, per esempio instagram, facebook, linkedin-page, x.
                </p>
              </div>
              {Object.values(BRANDS).map((itemBrand) => (
                <div key={itemBrand.id} className="space-y-2">
                  <h4 className="text-sm font-medium text-admin-text">{itemBrand.label}</h4>
                  {(integrations[itemBrand.id] || []).map((row, index) => (
                    <div key={`${itemBrand.id}-${index}`} className="grid gap-2 md:grid-cols-[1fr_10rem_1fr_auto]">
                      <Input aria-label={`Id canale ${itemBrand.label}`} value={row.id} onChange={(event) => updateRow(itemBrand.id, index, { id: event.target.value })} placeholder="id integrazione" />
                      <Input aria-label={`Piattaforma ${itemBrand.label}`} value={row.provider} onChange={(event) => updateRow(itemBrand.id, index, { provider: event.target.value })} placeholder="instagram" />
                      <Input aria-label={`Nome canale ${itemBrand.label}`} value={row.label} onChange={(event) => updateRow(itemBrand.id, index, { label: event.target.value })} placeholder="Nome visibile" />
                      <Button type="button" variant="outline" onClick={() => setIntegrations((current) => ({
                        ...current,
                        [itemBrand.id]: current[itemBrand.id].filter((_, rowIndex) => rowIndex !== index),
                      }))}>Togli</Button>
                    </div>
                  ))}
                  <Button type="button" size="sm" variant="outline" onClick={() => setIntegrations((current) => ({
                    ...current,
                    [itemBrand.id]: [...(current[itemBrand.id] || []), { id: "", provider: "", label: "" }],
                  }))}>Aggiungi canale</Button>
                </div>
              ))}
              <Button type="button" onClick={save} disabled={busy === "save"}>Salva canali</Button>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-3 p-6">
              <h3 className="font-medium text-admin-text">Accesso dei bot Grok</h3>
              <p className="text-sm text-admin-muted">
                Il testo da incollare nel bot è in Procedura bot. Il token condiviso vale per tutti i marchi.
                {meta?.bot_token
                  ? " Il valore è qui sotto."
                  : meta?.bot_token_preview
                    ? ` Quello già in uso (${meta.bot_token_preview}) non si può più leggere: per un solo marchio, per esempio R&CO Group, generalo dalla procedura senza scollegare gli altri.`
                    : ""}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" onClick={() => {
                  const params = new URLSearchParams(window.location.search);
                  params.set("module", "social-marketing-procedura");
                  params.delete("tab");
                  window.location.assign(`${window.location.pathname}?${params.toString()}`);
                }}>Apri la procedura</Button>
                <Button type="button" variant="outline" onClick={regenerate} disabled={busy === "token"}>Genera token per tutti i marchi</Button>
              </div>
              {meta?.bot_token || freshToken ? (
                <div className="space-y-1">
                  <Label htmlFor="fresh-token">Token condiviso</Label>
                  <Input id="fresh-token" readOnly value={meta?.bot_token || freshToken} onFocus={(event) => event.target.select()} />
                </div>
              ) : null}
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
}
